/**
 * useUpdateVolume Hook
 *
 * Custom React Query mutation hook for updating volume resources.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { volumeService } from '../../../api/volume.service';
import type { VolumeUpdateDTO } from '../../../types/volume.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateVolumeOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating volume
 * @param tenantId - Tenant ID
 * @param volumeId - Volume ID
 * @param options - Success/error callbacks
 */
export const useUpdateVolume = (
  tenantId: string,
  volumeId: string,
  options?: UseUpdateVolumeOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VolumeUpdateDTO) => volumeService.updateVolume(tenantId, volumeId, data),
    onSuccess: () => {
      // Invalidate both list and details queries
      queryClient.invalidateQueries({ queryKey: ['volumes', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['volumes', tenantId, volumeId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
